import { isDefined } from "@ciaopeople/cp-shatsu/TypeChecking";

import { Semaphore } from "semaphore-promise";
import { MediaId, MediaInfoRawData } from "../../model/MediaInfo.js";
import { MediaPlaylist } from "../../model/MediaPlaylist.js";
import { FilterablePlaylist, MediaElementTransformer, applyPlaylistTransformers } from "./FilterablePlaylist.js";


export type DataAppendPolicy<MI extends MediaInfoRawData<any>, T extends FilterablePlaylist<MI, any>> = (subject: T, incomingData: MediaPlaylist<any>, filtered: MI[]) => MI[];

export const defaultDataAppendPolicy: DataAppendPolicy<any, FilterablePlaylist<any, any>> = (playlist: FilterablePlaylist<any, any>, incomingData: MediaPlaylist<any>, filtered: MediaInfoRawData<any>[]): MediaInfoRawData<any>[] => {
    return filtered;
}
export abstract class AbstractFilterablePlaylist<T extends AbstractFilterablePlaylist<T, MI>, MI extends MediaInfoRawData<any>> implements FilterablePlaylist<MI, T> {

    protected currentMedias: MI[];
    protected currentIdx: number;
    protected lastName: string;
    protected lastNextUrl: string;

    protected semaphore: Semaphore;

    private transformers: MediaElementTransformer<MI>[];
    private idMap: Record<MediaId, number>;


    constructor(initialData?: MediaPlaylist<MI>, private appendPolicy: DataAppendPolicy<MI, AbstractFilterablePlaylist<T, MI>> = defaultDataAppendPolicy) {
        this.semaphore = new Semaphore(1);
        this.transformers = [];
        if (initialData) {
            this.lastName = initialData.name;
            this.lastNextUrl = initialData.nextUrl;
            this.currentMedias = initialData.elements;
        }
        else {
            this.lastName = "NonInitialized";
            this.lastNextUrl = null;
            this.currentMedias = [];
        }
        this.currentIdx = 0;
        this.rebuildMap();
    }

    private rebuildMap() {
        this.idMap = {};
        this.currentMedias.forEach((vid, idx) => this.idMap[vid.id] = idx);
    }


    /**
     * This method is called just before seeking playlist cursor to specified position.
     * Default implementation does nothing
     * if you need to block upcoming position switch you're allowed to acquire internal
     * semaphore inside this method
     * @param pos 
     */
    protected onBeforeSeek(pos: number): void {

    }



    private onBeforeAppend(incoming: MediaPlaylist<MI>, transformedVideos: MI[]): MI[] {
        if (isDefined(this.appendPolicy)) return this.appendPolicy(this, incoming, transformedVideos);
        return transformedVideos;
    }

    protected insecureAppend(playlist: MediaPlaylist<MI>): void {
        this.lastName = playlist.name;
        this.lastNextUrl = playlist.nextUrl;
        const toAppend = this.onBeforeAppend(playlist,
            applyPlaylistTransformers(this.transformers, playlist.elements));
        const initialIdx = this.length;
        toAppend.forEach((vid, idx) => this.idMap[vid.id] = (idx + initialIdx));
        this.currentMedias = this.currentMedias.concat(toAppend);
    }


    protected appendElements(playlist: MediaPlaylist<MI>): Promise<void> {
        return this.semaphore.acquire().then((release) => {
            this.insecureAppend(playlist);
            release();
        });
    }

    protected abstract newInstanceForClone(): T;


    public addTransformers(filters: MediaElementTransformer<MI>[]): T {
        const nuFilters = this.transformers.concat(filters);
        const nuPlaylist = this.newInstanceForClone();
        nuPlaylist.transformers = nuFilters;
        nuPlaylist.insecureAppend({
            name: this.name,
            nextUrl: this.nextUrl,
            elements: this.elements
        });
        return nuPlaylist;
    }

    public addTransformer(filter: MediaElementTransformer<MI>): T {
        return this.addTransformers([filter]);
    }


    get nextUrl(): string {
        return this.lastNextUrl;
    }

    get name(): string {
        return this.lastName;
    }

    get length(): number {
        return this.currentMedias.length;
    }
    get currentIndex(): number {
        return this.currentIdx;
    }
    get currentMedia(): MI {
        return this.currentMedias[this.currentIdx];
    }
    get elements(): MI[] {
        return this.currentMedias;
    }

    getResourceByPos(pos: number): Promise<MI> {
        return this.semaphore.acquire().then((release) => {
            let retval: MI;
            if (pos >= 0 && pos < this.length) {
                retval = this.elements[pos];
            }
            release();
            return retval;
        });
    }

    getPosById(videoId: MediaId): number {
        return this.elements.findIndex(e => e.id == videoId)
    }


    goto(pos: number): Promise<MI> {
        this.onBeforeSeek(pos);
        return this.getResourceByPos(pos).then(vinfo => {
            this.currentIdx = pos;
            return vinfo;
        });
    }

    private getPositionById(videoId: MediaId): Promise<number> {
        return this.semaphore.acquire().then((release) => {
            const pos = videoId in this.idMap ? this.idMap[videoId] : -1;
            release();
            return pos;
        })
    }

    seek(videoId: MediaId): Promise<MI> {
        return this.getPositionById(videoId).
            then(pos => this.goto(pos));
    }

    getById(videoId: MediaId): Promise<MI> {
        return this.getPositionById(videoId).then(pos => {
            return this.getResourceByPos(pos)
        });
    }

    getNextMedia(): Promise<MI> {
        return this.getResourceByPos(this.currentIdx + 1)
    }


    getPrevMedia(): Promise<MI> {
        return this.getResourceByPos(this.currentIdx - 1)
    }

    gotoNext(): Promise<MI> {
        return this.goto(this.currentIdx + 1);
    }

    gotoPrev(): Promise<MI> {
        return this.goto(this.currentIdx - 1);
    }

    gotoFirst(): Promise<MI> {
        return this.goto(0);
    }

}